import type { ReactNode } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { useSession } from '../useSession'

/**
 * Gates a route behind a session.
 *
 * Convenience only — it decides what to render, never what is allowed. The
 * server re-checks every request against the account (D-73), so bypassing this
 * in a debugger reveals nothing and grants nothing.
 */
export default function RequireSession({ children }: { children: ReactNode }) {
  const { session, isLoading } = useSession()
  const location = useLocation()

  /*
   * Nothing while the session is still in flight. Redirecting here would bounce
   * a signed-in user to the login screen on every refresh, because "not loaded
   * yet" and "not signed in" are the same value until the request lands.
   */
  if (isLoading) return null

  if (!session) {
    // Carries where they were headed, so the link they followed still works
    // after signing in.
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/signin?next=${next}`} replace />
  }

  /*
   * The studio app is not usable from a tablet on the wall (D-233).
   *
   * Here rather than on each route, because it is true of all of them at once
   * and a per-route list is a list somebody will add to and forget. The server
   * refuses these endpoints regardless — this exists so the refusal reads as a
   * sentence instead of as an empty table or a raw 403.
   *
   * `ViewPeople` is inside the kiosk ceiling, so a capability check cannot do
   * this job: `/admin/dancers` would pass on the client and fail on the server,
   * which is exactly the split that made the tablet look like the studio app in
   * the first place.
   */
  if (session.isKiosk) {
    return (
      <div className="mx-auto w-full max-w-[34rem] px-5 py-16 text-center">
        <div className="mx-auto grid size-11 place-items-center rounded-xl bg-surface-2 text-ink-3">
          <Lock size={19} strokeWidth={2} />
        </div>

        <h1 className="mt-4 font-display text-xl">This is a studio tablet</h1>

        <p className="mt-2 text-[13.5px] leading-relaxed text-ink-3">
          It is unlocked for taking attendance, and nothing else. To reach the rest of the studio
          system, sign in on your own phone or computer.
        </p>

        <Link
          to="/room"
          className="mt-6 inline-block rounded-xl bg-accent px-6 py-3 text-[15px] font-semibold text-on-accent no-underline"
        >
          Back to the classes
        </Link>
      </div>
    )
  }

  return <>{children}</>
}
